/**
 * Нормализация value стикеров с API.
 * Для `crm_lead` / `external_reference` бэк отдаёт объект, а не строку.
 */

export const TASK_TRACKER_CRM_LEAD_SYSTEM_KEY = 'crm_lead'

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const isTaskTrackerCrmLeadSticker = (
  fieldType: string,
  systemKey?: string | null,
) =>
  String(systemKey ?? '').trim() === TASK_TRACKER_CRM_LEAD_SYSTEM_KEY ||
  fieldType === 'external_reference'

/** Подпись лида в дереве выбора (имя → телефон → email → #id). */
export const formatCrmLeadTreeLeadLabel = (lead: {
  leadId: number
  name: string | null
  phone: string | null
  email: string | null
}) => {
  const name = lead.name?.trim()
  if (name) return name
  const phone = lead.phone?.trim()
  if (phone) return phone
  const email = lead.email?.trim()
  if (email) return email
  return `#${lead.leadId}`
}

export const formatCrmLeadTreeFunnelLabel = (funnel: {
  funnelId: number
  funnelName: string | null
}) => funnel.funnelName?.trim() || `Воронка #${funnel.funnelId}`

export const formatCrmLeadTreeDealLabel = (deal: {
  leadFunnelId: number
  dealName: string | null
  statusName: string | null
}) => {
  const dealName = deal.dealName?.trim()
  if (dealName) return dealName
  const statusName = deal.statusName?.trim()
  if (statusName) return statusName
  return `Сделка #${deal.leadFunnelId}`
}

const readPositiveId = (value: unknown): number | null => {
  const id = Number(value)
  if (!Number.isFinite(id) || id <= 0) return null
  return Math.trunc(id)
}

/** `lead.id` из value объекта стикера «Лид». */
export const extractCrmLeadStickerLeadId = (raw: unknown): number | null => {
  if (!isPlainObject(raw) || !isPlainObject(raw.lead)) return null
  return readPositiveId(raw.lead.id)
}

/** `funnel.id` из value объекта стикера «Лид». */
export const extractCrmLeadStickerFunnelId = (raw: unknown): number | null => {
  if (!isPlainObject(raw) || !isPlainObject(raw.funnel)) return null
  return readPositiveId(raw.funnel.id)
}

/** URL карточки лида в CRM (для открытия в новой вкладке). */
export const buildTaskTrackerCrmLeadCardHref = (
  leadId: number,
  funnelId?: number | null,
): string => {
  const params = new URLSearchParams()
  params.set('lead-id', String(Math.trunc(leadId)))

  if (funnelId != null) {
    const numericFunnelId = Number(funnelId)
    if (Number.isFinite(numericFunnelId) && numericFunnelId > 0) {
      params.set('funnel-id', String(Math.trunc(numericFunnelId)))
    }
  }

  return `/crm?${params.toString()}`
}

export const openTaskTrackerCrmLeadCardInNewTab = (
  leadId: number,
  funnelId?: number | null,
) => {
  window.open(
    buildTaskTrackerCrmLeadCardHref(leadId, funnelId),
    '_blank',
    'noopener,noreferrer',
  )
}

/** ФИО / имя лида из value стикера «Лид». */
export const formatCrmLeadStickerDisplayName = (
  raw: unknown,
): string | null => {
  if (raw == null) return null

  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed || trimmed === '[object Object]') return null
    if (trimmed.startsWith('{')) {
      try {
        return formatCrmLeadStickerDisplayName(JSON.parse(trimmed))
      } catch {
        return trimmed
      }
    }
    return trimmed
  }

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw)
  }

  if (!isPlainObject(raw)) return null

  const lead = raw.lead
  if (isPlainObject(lead)) {
    const name = lead.name
    if (name != null && String(name).trim() !== '') {
      return String(name).trim()
    }
  }

  if (raw.name != null && String(raw.name).trim() !== '') {
    return String(raw.name).trim()
  }

  return null
}

export const normalizeTaskTrackerStickerApiValue = (
  fieldType: string,
  systemKey: string | null | undefined,
  raw: unknown,
): string | null => {
  if (raw == null) return null

  if (isTaskTrackerCrmLeadSticker(fieldType, systemKey)) {
    return formatCrmLeadStickerDisplayName(raw)
  }

  if (fieldType === 'performer') {
    return null
  }

  if (typeof raw === 'object') {
    return null
  }

  const asString = String(raw).trim()
  return asString || null
}
